import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { injectable } from 'inversify';
import 'reflect-metadata';

import {
  ApiToken,
  BatchPayload,
  BatchResponse,
  GatewayOptions,
  HttpError,
  HttpResponse,
  IApiGateway,
  InstanceConfig,
  fetchOptions,
} from './api-gateway.types';

export const isErrorResponse = (response: HttpResponse | HttpError): boolean => {
  if (axios.isAxiosError(response)) {
    return true;
  }

  return response.status >= 400;
};

@injectable()
export class ApiGateway implements IApiGateway {
  private axiosInstance: AxiosInstance | null = null;

  private axiosInstances: Map<
    string,
    {
      instance: ApiGateway;
      config: InstanceConfig;
      options?: GatewayOptions;
    }
  > = new Map();

  private credentials: {
    apiUrl: string;
    token: ApiToken;
    company: string;
  } | null;

  constructor() {
    this.credentials = null;
  }

  get authorization(): string {
    if (this.credentials === null) {
      throw new Error('Credentials not set');
    }

    return this.buildAuthorizationHeader(this.credentials.token);
  }

  get company(): string {
    if (this.credentials === null) {
      throw new Error('Credentials not set');
    }

    return this.credentials.company;
  }

  private buildAuthorizationHeader(token: ApiToken): string {
    return `${token.type} ${token.token}`;
  }

  private getRequestHeaders(
    options: fetchOptions = {
      requireCredentials: true,
      headers: {},
    }
  ): Record<string, string> {
    const { requireCredentials = true } = options;

    if (requireCredentials && this.credentials === null) {
      throw new Error('Credentials not set');
    }

    return {
      Accept: 'application/json',
      ...(requireCredentials && {
        Authorization: this.authorization,
      }),
      'Content-Type': 'application/json',
      ...options.headers,
    };
  }

  public configure(
    apiUrl: string,
    token: ApiToken,
    company: string,
    options?: {
      defaultHeaders?: Record<string, string>;
      interceptor?: {
        responseInterceptor: (response: AxiosResponse) => AxiosResponse;
      };
    }
  ): void {
    this.credentials = {
      apiUrl,
      token,
      company,
    };

    this.axiosInstance = axios.create({
      baseURL: apiUrl,
      headers: {
        ...options?.defaultHeaders,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (options?.interceptor?.responseInterceptor) {
      const { responseInterceptor } = options.interceptor;

      if (responseInterceptor) {
        this.axiosInstance.interceptors.response.use(responseInterceptor, (error) =>
          responseInterceptor(error.response)
        );
      }
    }

    this.axiosInstances.forEach(({ instance, config, options }) => {
      instance.configure(
        config.apiUrl ?? apiUrl,
        config.token ?? token,
        config.company ?? company,
        options
      );
    });
  }

  public registerInstance(
    name: string,
    config: Partial<InstanceConfig>,
    options?: GatewayOptions
  ): void {
    if (this.credentials === null || this.axiosInstance === null) {
      throw new Error('Credentials not set');
    }

    const instance = new ApiGateway();
    const instanceConfig: InstanceConfig = {
      apiUrl: config.apiUrl ?? this.credentials.apiUrl,
      token: config.token ?? this.credentials.token,
      company: config.company ?? this.credentials.company,
    };

    instance.configure(
      instanceConfig.apiUrl,
      instanceConfig.token,
      instanceConfig.company,
      options
    );

    this.axiosInstances.set(name, {
      instance,
      config: instanceConfig,
      options,
    });
  }

  public use(name: string): ApiGateway {
    const instance = this.axiosInstances.get(name);

    if (!instance) {
      throw new Error('Instance not found');
    }

    return instance.instance;
  }

  public async fetch(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'OPTIONS' | 'HEAD' | 'CONNECT' | 'TRACE',
    endpoint: string,
    payload?: unknown,
    options: fetchOptions = {
      requireCredentials: true,
      headers: {},
    }
  ): Promise<HttpResponse> {
    const { requireCredentials, headers } = options;

    if (requireCredentials && (this.credentials === null || this.axiosInstance === null)) {
      throw new Error('Credentials not set');
    }

    const trimmedEndpoint = endpoint.replace(/^\//, '');

    const config: AxiosRequestConfig = {
      method,
      url: trimmedEndpoint,
      data: payload,
      headers: {
        ...this.getRequestHeaders(options),
        ...(requireCredentials ? {} : { 'Content-Type': 'application/json' }),
        ...headers,
      },
    };

    try {
      const response = await (this.axiosInstance?.request(config) ?? axios.request(config));

      if (!response) {
        throw new Error('No response from server');
      }

      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return (error as HttpError).response as HttpResponse;
      }

      throw new Error('Error making request');
    }
  }

  public createQueryParams(data?: Record<string, unknown>) {
    if (!data) {
      return '';
    }

    const params = new URLSearchParams(data as Record<string, string>);

    return params.toString();
  }

  public async batch(payload: BatchPayload[]): Promise<BatchResponse[]> {
    if (this.credentials === null || this.axiosInstance === null) {
      throw new Error('Credentials not set');
    }

    const token = this.authorization;
    const batchEndpoint = 'business/batch';
    const payloadWithHeader = payload.map((item) => ({
      ...item,
      headers: {
        Accept: 'application/json',
        Authorization: token,
        'Content-Type': 'application/json',
        'X-Requested-With': 'Fetch',
      },
    }));

    const response = await this.fetch(
      'POST',
      `${batchEndpoint}`,
      JSON.stringify(payloadWithHeader)
    );

    return response.data as BatchResponse[];
  }

  public isRequestFailed(response: AxiosResponse): boolean {
    return isErrorResponse(response);
  }
}
